import { AsyncPipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { serverTimestamp } from '@angular/fire/firestore';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { Observable, of, Subject, takeUntil } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { AppExceptionModel } from '../../../../models/app-exception.model';
import { Lesson } from '../../../../models/lesson.school.model';
import { UserModel } from '../../../../models/user.model';
import { SchoolLessonService } from '../../../../services/lesson.school.service';
import * as lessonActions from '../../../../state/actions/lesson.school.actions';
import { AppState } from '../../../../state/appstate';
import * as groupSelector from '../../../../state/selectors/group.selector';
import * as lessonSelector from '../../../../state/selectors/lesson.school.selector';
import { BtnActionComponent } from '../../../components/buttons/btn-action/btn-action.component';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';
import { RichTextEditorComponent } from '../../../components/quill-editor/rich-text-editor/rich-text-editor.component';

@Component({
  selector: 'app-detail-lesson',
  standalone: true,
  imports: [
    BtnBackComponent,
    BtnActionComponent,
    AsyncPipe,
    RichTextEditorComponent,
  ],
  templateUrl: './detail-lesson.component.html',
  styleUrl: './detail-lesson.component.css',
})
export class DetailLessonComponent implements OnInit, OnDestroy {
  activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  store: Store<AppState> = inject(Store<AppState>);
  toastr: ToastrService = inject(ToastrService);
  lessonService: SchoolLessonService = inject(SchoolLessonService);

  htmlData: string = '';
  // @ViewChild('textEditor', { static: false }) textEditor!: TextEditorComponent;

  groupId: string | null = this.activatedRoute.snapshot.paramMap.get('id');
  lessonId: string | null =
    this.activatedRoute.snapshot.paramMap.get('lessonId');

  user$: Observable<UserModel | null> = new Observable();
  lesson: Lesson | null | undefined;

  myRoles$: Observable<any> = new Observable();

  loading$: Observable<boolean> = new Observable();

  working: boolean = false;

  exit$: Subject<void> = new Subject<void>();

  constructor() {
    this.myRoles$ = this.store.select(
      groupSelector.getMyRolesInGroup(this.groupId),
    );

    this.activatedRoute.paramMap.subscribe((params) => {
      this.groupId = params.get('id');
      this.lessonId = params.get('lessonId');
    });

    this.user$ = this.store.select((state) => state.user.user);
  }

  ngOnInit(): void {
    this.store
      .select(
        lessonSelector.getLessonById({
          lessonId: this.lessonId,
          groupId: this.groupId,
        }),
      )
      .pipe(takeUntil(this.exit$))
      .subscribe(async (lesson) => {
        if (lesson == undefined || lesson == null) {
          this.store.dispatch(
            lessonActions.getLesson({
              groupId: this.groupId!,
              lessonId: this.lessonId!,
            }),
          );
        }
        if (this.lesson?.notesUrl || !lesson?.notesUrl) {
          // console.debug(
          //   `DetailLesson :: ngOnInit: NotesUrl already exists or not available.`
          // );
          this.lesson = lesson;
          return;
        }
        this.lesson = lesson;
        try {
          this.loading$ = of(true);
          const response = await this.lessonService.getNotes(
            `${this.lesson?.notesUrl!}?ts=${this.lesson?.modified?.seconds}`,
          );
          if (response) {
            // console.log(response);
            this.htmlData = JSON.stringify(response);
          }
          this.loading$ = of(false);
        } catch (error) {
          // console.error(
          //   `DetailLesson :: ngOnInit: Error fetching lesson notes: ${error}`
          // );
          this.toastr.error(
            'Error occurred while fetching lesson notes. Please try again later.',
            'Error Fetching Notes',
          );
          this.loading$ = of(false);
          return;
        }
      });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  onContentChanged(event: any): void {
    this.htmlData = event;
  }

  async onPublish(): Promise<void> {
    this.loading$ = of(true);

    try {
      // call two promises and wait for them to complete
      const [response, lessonData] = await Promise.all([
        await this.lessonService.getUploadUrl({
          groupId: this.groupId,
          id: this.lessonId,
          fileExtension: 'json',
          metadata: {
            id: this.lessonId,
            groupId: this.groupId,
          },
        }),
        this.htmlData,
      ]);

      // Call AWS SignedUrl to upload the lesson
      this.lessonService
        .uploadNotes(response.url, lessonData)
        .then((_) => {
          // console.log(`Uploaded Lesson Notes Url: ${JSON.stringify(response)}`);
          this.store.dispatch(
            lessonActions.update({
              groupId: this.groupId!,
              lessonId: this.lessonId!,
              lesson: {
                notesUrl: `https://${environment['AWS_GROUPS_CLOUDFRONT']!}/${
                  response.path
                }`,
                modified: serverTimestamp(),
              } as Lesson,
            }),
          );
        })
        .catch((error: any) => {
          this.toastr.error(
            `An error occurred while uploading lesson notes. Please try again later.`,
            'Error Publishing Notes',
          );
          this.store.dispatch(
            lessonActions.updateFailure({
              error: {
                code: 'Lesson Notes Upload Error',
                title: 'Error Publishing Notes',
                message: `An error occurred, '${error.message}'. Please try again later.`,
              } as AppExceptionModel,
            }),
          );
        });
      this.loading$ = of(false);
    } catch (error) {
      this.toastr.error(
        `An error occurred during notes upload. Please try again later.`,
        'Error Publishing Notes',
      );
      this.store.dispatch(
        lessonActions.updateFailure({
          error: {
            code: 'Lesson Notes Upload Error',
            title: 'Error Publishing Notes',
            message: `An error occurred while uploading notes. Please try again later.`,
          } as AppExceptionModel,
        }),
      );
      this.loading$ = of(false);
    }
  }
}
